import ActionTypes from './types'
import {
  getAllAmenitys,
  updateOnlyAmenity,
  listAmenityReservationsByBuilding,
  createNewAmenityReservation,
  updateExistingAmenityReservation,
  cancelReservation,
  approveReservation,
  declineReservation,
  updateLocalAmenityReservationRecordOnly,
  chargeResidentForAmenityReservation,
  refundPayment,
  createLedgerCharge,
} from '../lib/queries'
import {CACHE_ActiveBuildingId} from '../constants/AppConstants'
import moment from 'moment-timezone'

const LEDGER_CHARGE_CODE = 'club'

/**
 * Loads amenities for one or more buildings and dispatches to the store.
 * Supports fetching from multiple buildings (including sister buildings).
 *
 * @param {Dispatch} dispatch
 * @param {string|string[]} buildingIds - A single building ID or array of building IDs
 * @return {Promise<Array<*>>}
 */
export const loadAmenitiesForBuilding = async (dispatch, buildingIds) => {
  // Normalize to array for consistent handling
  const buildingIdArray = Array.isArray(buildingIds) ? buildingIds : [buildingIds]

  // Fetch amenities from all buildings in parallel
  const amenityPromises = buildingIdArray.map(buildingId => getAllAmenitys(buildingId))
  const amenityResults = await Promise.all(amenityPromises)

  // Flatten all amenities from all buildings
  let response = amenityResults.flat()

  // if we changed buildings between requests, we don't want to update the state
  // Check against the first building ID in the array (the primary building)
  if (buildingIdArray[0] !== localStorage[CACHE_ActiveBuildingId]) {
    return []
  }

  dispatch({
    type: ActionTypes.AMENITIES_LOAD_ALL,
    payload: response,
  })

  return response
}

/**
 * Loads amenity reservations for one or more buildings and dispatches to the store.
 * Supports fetching from multiple buildings (including sister buildings).
 *
 * @param {Dispatch} dispatch
 * @param {string|string[]} buildingIds - A single building ID or array of building IDs
 * @return {Promise<Array<*>>}
 */
export async function loadAmenityReservationsForBuilding(dispatch, buildingIds) {
  // Normalize to array for consistent handling
  const buildingIdArray = Array.isArray(buildingIds) ? buildingIds : [buildingIds]

  // Fetch reservations from all buildings in parallel
  const reservationPromises = buildingIdArray.map(buildingId => listAmenityReservationsByBuilding(buildingId))
  const reservationResults = await Promise.all(reservationPromises)

  // Flatten all reservations from all buildings
  let response = reservationResults.flat()

  // if we changed buildings between requests, we don't want to update the state
  // Check against the first building ID in the array (the primary building)
  if (buildingIdArray[0] !== localStorage[CACHE_ActiveBuildingId]) {
    return []
  }

  dispatch({
    type: ActionTypes.AMENITIES_LOAD_RESERVATIONS,
    payload: response,
  })

  return response
}

/**
 * @param {Dispatch} dispatch
 * @param {string} amenityId
 * @param {boolean} isVisible
 * @return {Promise<*>}
 */
export const setAmenityIsVisible = async (dispatch, amenityId, isVisible) => {
  let response = await updateOnlyAmenity({id: amenityId, isVisible: isVisible})

  dispatch({
    type: ActionTypes.AMENITIES_UPDATE_SINGLE,
    payload: response,
  })

  return response
}

/**
 * @param {Dispatch} dispatch
 * @param {*} amenityReservationInput
 * @return {Promise<*>}
 */
export async function createAmenityReservation(dispatch, amenityReservationInput) {
  const resp = await createNewAmenityReservation(amenityReservationInput)

  dispatch({
    type: ActionTypes.AMENITY_RESERVATIONS_CREATE_NEW,
    payload: resp,
  })

  return resp
}

/**
 * @param {Dispatch} dispatch
 * @param {*} amenityReservationInput
 * @return {Promise<*>}
 */
export async function updateAmenityReservation(dispatch, amenityReservationInput) {
  const resp = await updateExistingAmenityReservation(amenityReservationInput)

  dispatch({
    type: ActionTypes.AMENITY_RESERVATIONS_UPDATE_EXISTING,
    payload: resp,
  })

  return resp
}

/**
 * @param {Dispatch} dispatch
 * @param {string} amenityReservationId
 * @return {Promise<*>}
 */
export async function cancelAmenityReservation(dispatch, amenityReservationId) {
  const resp = await cancelReservation(amenityReservationId)

  dispatch({
    type: ActionTypes.AMENITY_RESERVATIONS_UPDATE_EXISTING,
    payload: resp,
  })

  return resp
}

/**
 * @param {Dispatch} dispatch
 * @param {string} amenityReservationId
 * @return {Promise<*>}
 */
export async function approveAmenityReservation(dispatch, amenityReservationId) {
  const resp = await approveReservation(amenityReservationId)

  dispatch({
    type: ActionTypes.AMENITY_RESERVATIONS_UPDATE_EXISTING,
    payload: resp,
  })

  return resp
}

/**
 * @param {Dispatch} dispatch
 * @param {string} amenityReservationId
 * @return {Promise<*>}
 */
export async function declineAmenityReservation(dispatch, amenityReservationId) {
  const resp = await declineReservation(amenityReservationId)

  dispatch({
    type: ActionTypes.AMENITY_RESERVATIONS_UPDATE_EXISTING,
    payload: resp,
  })

  return resp
}

/**
 * @param {Dispatch} dispatch
 * @param {{id: string, startAt?: Date|string, endAt?:Date|string, paymentMethodId?: string, notes?: string}} input
 * @return {Promise<*>}
 */
export async function updateAmenityReservationPaymentSettings(dispatch, input) {
  const resp = await updateLocalAmenityReservationRecordOnly(input)

  dispatch({
    type: ActionTypes.AMENITY_RESERVATIONS_UPDATE_EXISTING,
    payload: resp,
  })

  return resp
}

/**
 * @param {Dispatch} dispatch
 * @param {string} amenityReservationId
 * @return {Promise<*>}
 */
export async function chargeForAmenityReservation(dispatch, amenityReservationId) {
  let payment = await chargeResidentForAmenityReservation(amenityReservationId)

  setTimeout(() => {
    dispatch({
      type: ActionTypes.AMENITY_RESERVATIONS_MARK_AS_PAID,
      amenityReservationId: amenityReservationId,
      paymentId: payment ? payment.id : null, // if the charge is $0 there will not be a payment record
    })
  }, 2000) // slight delay to allow the payment complete message to show

  return payment
}

/**
 * @param {Dispatch} dispatch
 * @param {{id: string, notes: string, startAt: string, residentId: string}} reservation
 * @param {number} finalAmount
 * @param {string} staffId
 * @return {Promise<void>}
 */
export async function markAmenityReservationAsPaidInPerson(dispatch, reservation, finalAmount, staffId) {
  const charge = await createLedgerCharge(
    reservation.residentId,
    finalAmount,
    LEDGER_CHARGE_CODE,
    'Amenity Reservation Fee',
    `Final fee for reservation on ${moment(reservation.startAt).format('YYYY-MM-DD')}`,
  )

  const resp = await updateLocalAmenityReservationRecordOnly({
    id: reservation.id,
    reservationState: 'COMPLETED_PAID',
    // we want to record who marked it as paid in the notes
    notes: `${reservation.notes ? reservation.notes + '\n' : ''}Marked as "paid in person" by staff (${staffId}). Ledger charge id ${
      charge.chargeId
    }.`,
  })

  dispatch({
    type: ActionTypes.AMENITY_RESERVATIONS_UPDATE_EXISTING,
    payload: resp,
  })
}

/**
 * @param {Dispatch} dispatch
 * @param {string} residentId
 * @param {string} amenityReservationId
 * @param {string} depositPaymentId
 * @param {number?} amount
 * @param {number?} chargeDiff -- if this value exists and is greater than 0, a charge will be created for the difference
 * @return {Promise<*>}
 */
export async function refundAmenityReservationDeposit(dispatch, residentId, amenityReservationId, depositPaymentId, amount, chargeDiff) {
  // place the charge first, then refund the payment
  if (typeof chargeDiff === 'number' && chargeDiff > 0) {
    await createLedgerCharge(
      residentId,
      chargeDiff,
      LEDGER_CHARGE_CODE,
      'Amenity deposit forfeiture',
      `Portion of deposit forfeited for reservation ${amenityReservationId}`,
    )
  }

  const resp = await refundPayment(depositPaymentId, amount)

  dispatch({
    type: ActionTypes.AMENITY_RESERVATIONS_REFUND_DEPOSIT,
    amenityReservationId: amenityReservationId,
  })

  return resp
}
